import {
  Box,
  Checkbox,
  HStack,
  IconButton,
  Input,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';

import DevSandboxTooltip from 'components/DevSandbox/DevSandboxTooltip';
import Icon from 'components/Icon';
import Text from 'components/Text';

import {
  CVFontFamily,
  CVFontSize,
  CVPersonalInfo,
  CVSettings,
  CVTemplate,
  CV_ACCENT_COLORS,
  CV_FONT_OPTIONS,
} from '../types';

interface CVSettingsPopoverProps {
  settings: CVSettings;
  template: CVTemplate;
  personalInfo: CVPersonalInfo;
  onTemplateChange: (template: CVTemplate) => void;
  onFontChange: (fontFamily: CVFontFamily) => void;
  onFontSizeChange: (fontSize: CVFontSize) => void;
  onColorChange: (color: string) => void;
  onOpacityChange: (opacity: number) => void;
  onSidebarReversedToggle: () => void;
  onShowPhotoToggle: () => void;
  onHeaderBgToggle: () => void;
  onPersonalInfoChange: (field: keyof CVPersonalInfo, value: string | boolean) => void;
  isShared: boolean;
  shareUrl: string;
  onShareToggle: (shared: boolean) => void;
  onCopyShareLink: () => void;
  isShareToggling: boolean;
}

const TEMPLATES: { value: CVTemplate; label: string }[] = [
  { value: 'classic', label: 'Classic' },
  { value: 'modern', label: 'Modern' },
  { value: 'creative', label: 'Creative' },
];

const FONT_SIZES: { value: CVFontSize; label: string }[] = [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
];

const CVSettingsPopover = ({
  settings,
  template,
  personalInfo,
  onTemplateChange,
  onFontChange,
  onFontSizeChange,
  onColorChange,
  onOpacityChange,
  onSidebarReversedToggle,
  onShowPhotoToggle,
  onHeaderBgToggle,
  onPersonalInfoChange,
  isShared,
  shareUrl,
  onShareToggle,
  onCopyShareLink,
  isShareToggling,
}: CVSettingsPopoverProps) => {
  const isModern = template === 'modern';

  return (
    <Popover placement="bottom-end">
      <PopoverTrigger>
        <Box>
          <DevSandboxTooltip label="Design settings" placement="bottom">
            <IconButton
              aria-label="Design settings"
              icon={<Icon type="Settings" size={14} />}
              variant="ghost"
              size="xs"
              h="28px"
              w="28px"
              minW="28px"
              _hover={{ backgroundColor: 'whiteAlpha.100' }}
            />
          </DevSandboxTooltip>
        </Box>
      </PopoverTrigger>
      <PopoverContent
        width="300px"
        backgroundColor="gray.800"
        borderColor="whiteAlpha.100"
        zIndex="popover"
        boxShadow="0 8px 32px rgba(0,0,0,0.5)"
      >
        <PopoverBody p={0}>
          <VStack spacing={0} alignItems="stretch">
            {/* Template */}
            <Box px={4} pt={3} pb={2.5}>
              <Text fontSize="micro" opacity="tertiary" fontWeight="semibold" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Template
              </Text>
              <HStack spacing={2} mt={2}>
                {TEMPLATES.map((t) => (
                  <Box
                    key={t.value}
                    px={3}
                    py={1.5}
                    borderRadius="md"
                    cursor="pointer"
                    backgroundColor={template === t.value ? 'purple.600' : 'whiteAlpha.50'}
                    border="1px solid"
                    borderColor={template === t.value ? 'purple.400' : 'whiteAlpha.100'}
                    transition="all 0.15s"
                    _hover={{ borderColor: template === t.value ? 'purple.400' : 'whiteAlpha.300' }}
                    onClick={() => onTemplateChange(t.value)}
                  >
                    <Text fontSize="micro" fontWeight={template === t.value ? 'semibold' : 'regular'}>
                      {t.label}
                    </Text>
                  </Box>
                ))}
              </HStack>
            </Box>

            <Box height="1px" backgroundColor="whiteAlpha.100" />

            {/* Font */}
            <Box px={4} py={2.5}>
              <Text fontSize="micro" opacity="tertiary" fontWeight="semibold" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Font
              </Text>
              <HStack spacing={1.5} mt={2} flexWrap="wrap">
                {CV_FONT_OPTIONS.map((option) => (
                  <Box
                    key={option.value}
                    px={2.5}
                    py={1}
                    borderRadius="md"
                    cursor="pointer"
                    backgroundColor={settings.fontFamily === option.value ? 'whiteAlpha.200' : 'transparent'}
                    border="1px solid"
                    borderColor={settings.fontFamily === option.value ? 'whiteAlpha.300' : 'transparent'}
                    transition="all 0.15s"
                    _hover={{ backgroundColor: 'whiteAlpha.100' }}
                    onClick={() => onFontChange(option.value)}
                    fontFamily={option.value}
                  >
                    <Box style={{ fontSize: '12px' }}>
                      {option.label}
                    </Box>
                  </Box>
                ))}
              </HStack>
            </Box>

            <Box height="1px" backgroundColor="whiteAlpha.100" />

            {/* Font Size */}
            <Box px={4} py={2.5}>
              <Text fontSize="micro" opacity="tertiary" fontWeight="semibold" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Font Size
              </Text>
              <HStack spacing={0} mt={2} borderRadius="md" border="1px solid" borderColor="whiteAlpha.100" overflow="hidden">
                {FONT_SIZES.map((fs, i) => (
                  <Box
                    key={fs.value}
                    flex={1}
                    textAlign="center"
                    py={1.5}
                    cursor="pointer"
                    backgroundColor={settings.fontSize === fs.value ? 'purple.600' : 'transparent'}
                    borderRight={i < FONT_SIZES.length - 1 ? '1px solid' : 'none'}
                    borderColor="whiteAlpha.100"
                    transition="all 0.15s"
                    _hover={{ backgroundColor: settings.fontSize === fs.value ? 'purple.600' : 'whiteAlpha.50' }}
                    onClick={() => onFontSizeChange(fs.value)}
                  >
                    <Box style={{ fontSize: '11px', fontWeight: settings.fontSize === fs.value ? 600 : 400 }}>
                      {fs.label}
                    </Box>
                  </Box>
                ))}
              </HStack>
            </Box>

            <Box height="1px" backgroundColor="whiteAlpha.100" />

            {/* Accent color */}
            <Box px={4} py={2.5}>
              <Text fontSize="micro" opacity="tertiary" fontWeight="semibold" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Accent color
              </Text>
              <Wrap spacing={2} mt={2}>
                {CV_ACCENT_COLORS.map((color) => (
                  <WrapItem key={color}>
                    <Box
                      width="24px"
                      height="24px"
                      borderRadius="md"
                      backgroundColor={color}
                      cursor="pointer"
                      border="2px solid"
                      borderColor={settings.accentColor === color ? 'white' : 'transparent'}
                      transition="all 0.15s"
                      _hover={{ transform: 'scale(1.1)' }}
                      onClick={() => onColorChange(color)}
                    />
                  </WrapItem>
                ))}
              </Wrap>
              {/* Opacity / Intensity slider */}
              <HStack spacing={3} mt={3}>
                <Box style={{ fontSize: '11px', color: 'var(--chakra-colors-whiteAlpha-500)', whiteSpace: 'nowrap' }}>
                  Intensity
                </Box>
                <Slider
                  value={Math.round((settings.accentOpacity ?? 1) * 100)}
                  min={10}
                  max={100}
                  step={5}
                  onChange={(val) => onOpacityChange(val / 100)}
                  colorScheme="purple"
                  size="sm"
                >
                  <SliderTrack backgroundColor="whiteAlpha.200">
                    <SliderFilledTrack />
                  </SliderTrack>
                  <SliderThumb boxSize={3} />
                </Slider>
                <Box style={{ fontSize: '11px', color: 'var(--chakra-colors-whiteAlpha-500)', minWidth: '32px', textAlign: 'right' }}>
                  {Math.round((settings.accentOpacity ?? 1) * 100)}%
                </Box>
              </HStack>
            </Box>

            <Box height="1px" backgroundColor="whiteAlpha.100" />

            {/* Template-specific options + GDPR */}
            <Box px={4} py={2.5}>
              <Text fontSize="micro" opacity="tertiary" fontWeight="semibold" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }} mb={2}>
                Options
              </Text>
              <VStack spacing={2} alignItems="stretch">
                {isModern && (
                  <HStack
                    spacing={2}
                    px={2}
                    py={1.5}
                    borderRadius="md"
                    cursor="pointer"
                    _hover={{ backgroundColor: 'whiteAlpha.50' }}
                    onClick={onSidebarReversedToggle}
                  >
                    <Checkbox
                      isChecked={settings.sidebarReversed}
                      onChange={onSidebarReversedToggle}
                      colorScheme="purple"
                      size="sm"
                      pointerEvents="none"
                    />
                    <VStack spacing={0} alignItems="flex-start">
                      <Text fontSize="small">Reverse layout</Text>
                      <Box style={{ fontSize: '10px', color: 'var(--chakra-colors-whiteAlpha-400)' }}>
                        Moves the sidebar from left to right
                      </Box>
                    </VStack>
                  </HStack>
                )}
                <HStack
                  spacing={2}
                  px={2}
                  py={1.5}
                  borderRadius="md"
                  cursor="pointer"
                  _hover={{ backgroundColor: 'whiteAlpha.50' }}
                  onClick={onHeaderBgToggle}
                >
                  <Checkbox
                    isChecked={settings.headerBackground}
                    onChange={onHeaderBgToggle}
                    colorScheme="purple"
                    size="sm"
                    pointerEvents="none"
                  />
                  <VStack spacing={0} alignItems="flex-start">
                    <Text fontSize="small">{isModern ? 'Colored sidebar' : 'Colored header'}</Text>
                    <Box style={{ fontSize: '10px', color: 'var(--chakra-colors-whiteAlpha-400)' }}>
                      {isModern ? 'Uses accent color as sidebar background' : 'Uses accent color as header background'}
                    </Box>
                  </VStack>
                </HStack>
                <HStack
                  spacing={2}
                  px={2}
                  py={1.5}
                  borderRadius="md"
                  cursor="pointer"
                  _hover={{ backgroundColor: 'whiteAlpha.50' }}
                  onClick={onShowPhotoToggle}
                >
                  <Checkbox
                    isChecked={settings.showPhoto}
                    onChange={onShowPhotoToggle}
                    colorScheme="purple"
                    size="sm"
                    pointerEvents="none"
                  />
                  <VStack spacing={0} alignItems="flex-start">
                    <Text fontSize="small">Show photo</Text>
                    <Box style={{ fontSize: '10px', color: 'var(--chakra-colors-whiteAlpha-400)' }}>
                      Displays your photo or avatar on the CV
                    </Box>
                  </VStack>
                </HStack>
                <HStack
                  spacing={2}
                  px={2}
                  py={1.5}
                  borderRadius="md"
                  cursor="pointer"
                  _hover={{ backgroundColor: 'whiteAlpha.50' }}
                  onClick={() => onPersonalInfoChange('gdprConsent', !personalInfo.gdprConsent)}
                >
                  <Checkbox
                    isChecked={personalInfo.gdprConsent}
                    onChange={(e) => onPersonalInfoChange('gdprConsent', e.target.checked)}
                    colorScheme="purple"
                    size="sm"
                    pointerEvents="none"
                  />
                  <VStack spacing={0} alignItems="flex-start">
                    <Text fontSize="small">GDPR consent clause</Text>
                    <Box style={{ fontSize: '10px', color: 'var(--chakra-colors-whiteAlpha-400)' }}>
                      Adds EU data processing consent to CV footer
                    </Box>
                  </VStack>
                </HStack>
              </VStack>
            </Box>

            <Box height="1px" backgroundColor="whiteAlpha.100" />

            {/* Share */}
            <Box px={4} py={2.5} pb={3}>
              <Text fontSize="micro" opacity="tertiary" fontWeight="semibold" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }} mb={2}>
                Share
              </Text>
              <HStack
                spacing={2}
                px={2}
                py={1.5}
                borderRadius="md"
                cursor="pointer"
                _hover={{ backgroundColor: 'whiteAlpha.50' }}
                onClick={() => !isShareToggling && onShareToggle(!isShared)}
                opacity={isShareToggling ? 0.5 : 1}
              >
                <Checkbox
                  isChecked={isShared}
                  onChange={() => onShareToggle(!isShared)}
                  colorScheme="purple"
                  size="sm"
                  pointerEvents="none"
                  isDisabled={isShareToggling}
                />
                <VStack spacing={0} alignItems="flex-start">
                  <Text fontSize="small">Public share link</Text>
                  <Box style={{ fontSize: '10px', color: 'var(--chakra-colors-whiteAlpha-400)' }}>
                    Anyone with the link can view this CV
                  </Box>
                </VStack>
              </HStack>
              {isShared && (
                <HStack mt={2} spacing={1}>
                  <Input
                    value={shareUrl}
                    isReadOnly
                    size="xs"
                    variant="filled"
                    backgroundColor="whiteAlpha.100"
                    fontSize="xs"
                    borderRadius="md"
                    _hover={{}}
                    _focus={{}}
                  />
                  <DevSandboxTooltip label="Copy link" placement="top">
                    <IconButton
                      aria-label="Copy share link"
                      icon={<Icon type="Copy" size={12} />}
                      size="xs"
                      variant="ghost"
                      onClick={onCopyShareLink}
                      _hover={{ backgroundColor: 'whiteAlpha.100' }}
                    />
                  </DevSandboxTooltip>
                </HStack>
              )}
            </Box>
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default CVSettingsPopover;
